import React from "react";
import { ClipboardList, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";

interface CardWaitlistProps {
  centerName: string;
  countySlug: string;
  nhSlug: string;
  onTrackClick: (type: string) => void;
  isInLeads: boolean;
  setIsInLeads: (value: boolean) => void;
  email?: string;
}

export function CardWaitlist({ 
  centerName, 
  countySlug, 
  nhSlug, 
  onTrackClick,
  isInLeads,
  setIsInLeads,
  email
}: CardWaitlistProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sendEmail } = useTransactionalEmail();

  const handleExpressInterest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    onTrackClick('express_interest');
    
    if (user) {
      try {
        if (isInLeads) {
          toast.info("Already in your leads", {
            description: "You've already expressed interest in this nursing home",
            action: {
              label: "View Dashboard",
              onClick: () => navigate("/dashboard")
            }
          });
          return;
        }
        
        // Insert the lead record
        const { data: leadData, error } = await supabase.from('nursing_home_leads').insert({
          user_id: user.id,
          nursing_home_id: nhSlug,
          nursing_home_name: centerName,
          location: countySlug,
          status: 'interested',
          notes: 'Added from nursing homes directory'
        }).select();
        
        if (error) throw error;
        
        setIsInLeads(true);

        try {
          const claimLink = `${window.location.origin}/nursing-homes/${countySlug}/${nhSlug}?action=claim`;
          const leadId = leadData && leadData[0] ? leadData[0].id : undefined;
          
          // Send anonymous lead notification to nursing home
          await sendEmail({
            type: 'nursing_home_inquiry',
            data: {
              nursingHomeName: centerName,
              county: countySlug,
              message: "A family has expressed interest in your nursing home through Grandpal and would like to connect. Claim your profile to access this lead's contact information.",
              email: email || "hello@updates.grandpal.co",
              claimLink,
              leadId
            }
          });
          
          // Send confirmation email to user
          await sendEmail({
            type: 'inquiry_confirmation',
            data: {
              firstName: user.user_metadata?.first_name || 'User',
              email: user.email || '',
              nursingHomeName: centerName,
              county: countySlug,
            }
          });
        } catch (emailError) {
          console.error("Failed to send nursing home inquiry email:", emailError);
        }
        
        toast.success(`Added ${centerName} to your leads`, {
          description: "View all your saved nursing homes in your dashboard",
          action: {
            label: "View Dashboard",
            onClick: () => navigate("/dashboard")
          }
        });
      } catch (error) {
        console.error("Error adding to leads:", error);
        toast.error("Failed to add to your leads", {
          description: "Please try again or contact support"
        });
      }
    } else {
      navigate(`/waitlist?nursing_home=${encodeURIComponent(centerName)}&county=${encodeURIComponent(countySlug)}&nh_slug=${encodeURIComponent(nhSlug)}`);
      
      toast.info("Complete the form to express interest", {
        description: `You'll need to create an account to track ${centerName}.`
      });
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant="outline"
            className={`w-full border-grandpal-green text-grandpal-green hover:bg-grandpal-green hover:text-white ${
              isInLeads ? 'bg-grandpal-green text-white' : ''
            }`}
            onClick={handleExpressInterest}
            disabled={isInLeads}
          >
            {isInLeads ? (
              <>
                <Check className="h-4 w-4 mr-1" />
                Added to List
              </>
            ) : (
              <>
                <ClipboardList className="h-4 w-4 mr-1" />
                {user ? 'Add to List' : 'Join Waitlist'}
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isInLeads ? "You've expressed interest" : "Add this nursing home to your leads"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
